import { useCallback } from "react";
import { useBoardFilters } from "@contexts/BoardFiltersContext";
import type { SidebarFilterResetViewModel } from "./SidebarFilterReset.ViewModel.types";

/**
 * Clears every filter narrowing the board.
 *
 * The search box lives in the toolbar rather than the pane, but it is cleared
 * too: leaving a search term behind after "reset filters" would show a board
 * that is still obviously filtered.
 *
 * A null connection selection means "all", which is this board's way of saying
 * unfiltered — it is not an empty list.
 */
export const useSidebarFilterResetViewModel =
  (): SidebarFilterResetViewModel => {
    const {
      activeMetricFilters,
      clearMetricFilters,
      selectedTagFilters,
      clearTagFilters,
      selectedConnectionFilterIds,
      setSelectedConnectionFilterIds,
      searchTerm,
      setSearchTerm,
    } = useBoardFilters();

    const hasConnectionFilter = selectedConnectionFilterIds !== null;
    const hasSearch = searchTerm.trim() !== "";

    const activeCount =
      activeMetricFilters.length +
      selectedTagFilters.length +
      (hasConnectionFilter ? 1 : 0) +
      (hasSearch ? 1 : 0);

    const resetFilters = useCallback(() => {
      clearMetricFilters();
      clearTagFilters();
      setSelectedConnectionFilterIds(null);
      setSearchTerm("");
    }, [
      clearMetricFilters,
      clearTagFilters,
      setSelectedConnectionFilterIds,
      setSearchTerm,
    ]);

    return {
      hasActiveFilters: activeCount > 0,
      activeCount,
      resetFilters,
    };
  };
